import { z } from "zod";
// The contract of the staff catalog CMS (CHT-706): the routes R1 to R7 and W1 to W21 under
// `/staff/catalog`. The API validates every body and query with these schemas, the admin and the typed
// client validate with the same ones, and every response is parsed with them before it is served or
// used. Every top-level shape is STRICT: a key that is not named here fails the parse, so a new part of
// a view has to be named here before it is served. The detail view has NO `internalNotes` key. Notes
// have their own schema (`staffCatalogNotesSchema`) and their own route (R3).
//
// This package cannot import the domain package. The code tuples below repeat the domain's tuples, and
// `apps/api` has a parity test that fails when a tuple or a bound here differs from the domain's.
//
// Dates travel as ISO 8601 strings. A request date is parsed to a `Date` (the domain takes dates). A
// response date is accepted as a `Date` (the API validates before it serializes) or as an ISO string
// (the client validates after it parses), and is always a `Date` in the output.
// --- codes (parity-tested against the domain) ------------------------------------------------------
export const staffCatalogItemTypes = ["DINING", "VILLA", "EVENT", "EXPERIENCE", "SERVICE"];
export const staffCatalogItemStatuses = [
    "DRAFT",
    "REVIEW",
    "PUBLISHED",
    "UNPUBLISHED",
    "ARCHIVED",
];
export const staffCatalogPricingModels = [
    "UNKNOWN",
    "INFORMATION_ONLY",
    "FIXED",
    "FROM",
    "RANGE",
    "QUOTE_REQUIRED",
];
export const staffCatalogPriceUnits = [
    "PER_PERSON",
    "PER_GROUP",
    "PER_BOOKING",
    "PER_NIGHT",
    "PER_DAY",
    "PER_HOUR",
    "PER_ITEM",
];
export const staffCatalogPriceSources = [
    "SUPPLIER_CONTRACT",
    "SUPPLIER_PUBLISHED",
    "EDITORIAL_ESTIMATE",
    "IMPORTED",
];
export const staffCatalogPriceClasses = [
    "PAYMENT_GRADE",
    "FIXED_NOT_PAYABLE",
    "INDICATIVE",
    "QUOTE",
    "UNKNOWN",
];
export const staffCatalogFulfilmentModes = [
    "INFORMATION_ONLY",
    "HUMAN_CONCIERGE",
    "EXTERNAL_LINK",
    "CUSTOMER_SELF_SERVICE",
    "DIRECT_API",
];
export const staffCatalogSourceKinds = [
    "EDITORIAL",
    "IMPORT",
    "PROVIDER_ENRICHMENT",
    "LEGACY_MIGRATION",
];
export const staffCatalogTagNamespaces = [
    "CUISINE",
    "DIETARY",
    "AMBIENCE",
    "MEAL_PERIOD",
    "AMENITY",
    "FEATURE",
    "RECOMMENDED_FOR",
    "GENERAL",
];
export const staffCatalogPersonKinds = ["INDIVIDUAL", "GROUP"];
export const staffCatalogPersonRoles = [
    "PERFORMER",
    "HOST",
    "CHEF",
    "GUIDE",
    "ARTIST",
    "OTHER",
];
export const staffCatalogMediaKinds = ["IMAGE", "VIDEO"];
export const staffCatalogNoteKinds = [
    "EDITORIAL",
    "SUPPLIER_CONTACT",
    "PRICING_CONTEXT",
    "FULFILMENT",
    "VERIFICATION",
];
export const staffCatalogDressCodes = [
    "CASUAL",
    "SMART_CASUAL",
    "SMART",
    "FORMAL",
    "BLACK_TIE",
];
export const staffCatalogReservationPolicies = [
    "REQUIRED",
    "RECOMMENDED",
    "NOT_REQUIRED",
    "WALK_IN_ONLY",
];
export const staffCatalogEventStatuses = [
    "SCHEDULED",
    "POSTPONED",
    "CANCELLED",
    "SOLD_OUT",
];
export const staffCatalogSchedulePrecisions = ["EXACT", "DATE_ONLY"];
export const staffCatalogExperiencePrivacies = ["PRIVATE", "SHARED", "EITHER"];
export const staffCatalogPublishabilityIssueCodes = [
    "MISSING_SUBTYPE",
    "MISSING_SHORT_DESCRIPTION",
    "MISSING_DESCRIPTION",
    "MISSING_LOCATION",
    "MISSING_COVER_IMAGE",
    "MEDIA_ALT_TEXT_MISSING",
    "MISSING_PRIMARY_CATEGORY",
    "DINING_MISSING_CUISINE",
    "VILLA_MISSING_CAPACITY",
    "VILLA_TIMES_WITHOUT_ZONE",
    "EVENT_MISSING_VENUE",
    "EVENT_ENDED",
    "EXPERIENCE_MISSING_DURATION",
    "SERVICE_MISSING_AREA",
    "FULFILMENT_MODE_NOT_ENABLED",
    "EXTERNAL_LINK_MISSING",
    "PRICING_INCOMPLETE",
    "CUSTOMER_TEXT_CONTAINS_INTERNAL_NOTE",
    "EXTERNAL_URL_NOT_ALLOWED",
    "PRICE_AUTHORITY_EXPIRED",
];
/** The image types a staff member may upload (Phase 3.2: images only). */
export const staffCatalogUploadContentTypes = ["image/jpeg", "image/png", "image/webp"];
export const staffCatalogVerificationFilters = ["never", "stale", "fresh"];
export const staffCatalogSorts = ["updated", "name"];
// --- bounds (parity-tested against the domain) -----------------------------------------------------
export const STAFF_CATALOG_BOUNDS = Object.freeze({
    name: 200,
    slug: 120,
    shortDescription: 300,
    description: 8000,
    addressText: 300,
    locality: 100,
    region: 100,
    externalUrl: 2048,
    editorialPriority: 1000,
    priceTerms: 500,
    reason: 500,
    noteBody: 4000,
    mediaPath: 512,
    mediaAltText: 300,
    mediaCaption: 300,
    mediaCredit: 200,
    externalRefId: 255,
    categories: 20,
    tags: 60,
    people: 100,
    media: 100,
    tagLabel: 100,
    categoryName: 100,
    personName: 200,
    personBio: 1000,
    query: 100,
    limit: 100,
    // The name-sort cursor is base64url of JSON that holds the lower-cased name. The worst case is a name
    // of 200 control characters: 6 bytes each as `\u0001`, so about 1300 bytes and 1700 characters.
    cursor: 2048,
    uploadBytes: 10 * 1024 * 1024,
});
// --- primitives -------------------------------------------------------------------------------------
const id = z.string().min(1).max(64);
const code = z.string().min(1).max(64);
const nullableText = z.string().nullable();
const nullableNumber = z.number().nullable();
const count = z.number().int();
const responseInstant = z.union([
    z.date(),
    z.iso.datetime({ offset: true }).transform((value) => new Date(value)),
]);
const nullableResponseInstant = responseInstant.nullable();
const requestInstant = z.iso.datetime({ offset: true }).transform((value) => new Date(value));
const CODE = /^[a-z0-9_]{1,64}$/;
const SLUG = /^[a-z0-9]+(-[a-z0-9]+)*$/;
const COUNTRY = /^[A-Z]{2}$/;
const PROVIDER = /^[a-z][a-z0-9_]*$/;
const SOURCE_SYSTEM = /^[a-z][a-z0-9_]{0,63}$/;
const MEDIA_PATH = /^[A-Za-z0-9/_.-]+$/;
const TIME_OF_DAY = /^([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/;
const CURSOR = /^[A-Za-z0-9_-]+$/;
const codeValue = z.string().regex(CODE);
const slugValue = z.string().min(1).max(STAFF_CATALOG_BOUNDS.slug).regex(SLUG);
const optionalNullableText = (max) => z.string().min(1).max(max).nullish();
const whole = (min, max) => z.number().int().min(min).max(max).nullish();
/** A version token: the `version` of the detail the staff member last read. */
export const staffCatalogVersionSchema = z.number().int().positive();
const expectedVersion = staffCatalogVersionSchema;
// --- paths ------------------------------------------------------------------------------------------
export const STAFF_CATALOG_PATHS = Object.freeze({
    items: "/staff/catalog/items",
    categories: "/staff/catalog/categories",
    tags: "/staff/catalog/tags",
    people: "/staff/catalog/people",
});
/** What follows `/staff/catalog/items/:itemId/`. */
export const STAFF_CATALOG_ITEM_SUBPATHS = Object.freeze({
    details: "details",
    subtype: "subtype",
    pricing: "pricing",
    priceAuthorization: "price-authorization",
    categories: "categories",
    tags: "tags",
    people: "people",
    media: "media",
    mediaUploads: "media/uploads",
    externalRefs: "external-refs",
    verify: "verify",
    internalNotes: "internal-notes",
    history: "history",
    submitForReview: "submit-for-review",
    returnToDraft: "return-to-draft",
    publish: "publish",
    unpublish: "unpublish",
    archive: "archive",
});
/** The path of an item, or of one of its sub-resources. The id is encoded, so it cannot add a path segment. */
export function staffCatalogItemPath(itemId, subpath) {
    const base = `${STAFF_CATALOG_PATHS.items}/${encodeURIComponent(itemId)}`;
    return subpath === undefined ? base : `${base}/${subpath}`;
}
/** The same paths as Hono route templates (`:itemId`), for the API to register. */
export function staffCatalogItemRoute(subpath) {
    const base = `${STAFF_CATALOG_PATHS.items}/:itemId`;
    return subpath === undefined ? base : `${base}/${subpath}`;
}
// --- list query (R1) ---------------------------------------------------------------------------------
/**
 * `CATALOG_VERIFICATION_STALE_DAYS` in the domain: an item verified longer ago than this is "stale".
 * Repeated here for the admin's help text only; the API judges it with the domain's constant.
 */
export const STAFF_CATALOG_VERIFICATION_STALE_DAYS = 180;
const statusFilter = z
    .union([
    z.enum(staffCatalogItemStatuses),
    z.array(z.enum(staffCatalogItemStatuses)).min(1).max(staffCatalogItemStatuses.length),
])
    .transform((value) => (Array.isArray(value) ? value : [value]));
const limitQuery = z
    .string()
    .regex(/^\d{1,3}$/)
    .transform(Number)
    .pipe(z.number().int().min(1).max(STAFF_CATALOG_BOUNDS.limit));
const cursorQuery = z.string().min(1).max(STAFF_CATALOG_BOUNDS.cursor).regex(CURSOR);
const textQuery = z.string().min(1).max(STAFF_CATALOG_BOUNDS.query);
/**
 * The query of R1, as it arrives (strings, `status` once or repeated) and as the handlers read it
 * (typed). Unknown keys are refused. `featured` has one value, `true`: there is no "not featured"
 * filter. There is no total count: the browser pages with `cursor`.
 */
export const staffCatalogListQuerySchema = z.strictObject({
    type: z.enum(staffCatalogItemTypes).optional(),
    status: statusFilter.optional(),
    countryCode: z.string().regex(COUNTRY).optional(),
    locality: z.string().min(1).max(STAFF_CATALOG_BOUNDS.locality).optional(),
    verification: z.enum(staffCatalogVerificationFilters).optional(),
    pricingModel: z.enum(staffCatalogPricingModels).optional(),
    fulfilmentMode: z.enum(staffCatalogFulfilmentModes).optional(),
    sourceKind: z.enum(staffCatalogSourceKinds).optional(),
    sourceSystem: z.string().regex(SOURCE_SYSTEM).optional(),
    featured: z
        .literal("true")
        .transform(() => true)
        .optional(),
    q: textQuery.optional(),
    sort: z.enum(staffCatalogSorts).optional(),
    limit: limitQuery.optional(),
    cursor: cursorQuery.optional(),
});
export const staffCatalogHistoryQuerySchema = z.strictObject({
    limit: limitQuery.optional(),
    cursor: cursorQuery.optional(),
});
export const staffCatalogCategoryQuerySchema = z.strictObject({
    q: textQuery.optional(),
    limit: limitQuery.optional(),
});
export const staffCatalogTagQuerySchema = z.strictObject({
    namespace: z.enum(staffCatalogTagNamespaces).optional(),
    q: textQuery.optional(),
    limit: limitQuery.optional(),
});
export const staffCatalogPersonQuerySchema = z.strictObject({
    q: textQuery.optional(),
    limit: limitQuery.optional(),
    cursor: cursorQuery.optional(),
});
// --- request bodies: pricing, subtypes, links ---------------------------------------------------------
/** A price as a staff member types it. Omitted amounts, unit and dates mean "none". `noExpiry` defaults to false. */
export const staffCatalogPricingSchema = z
    .strictObject({
    model: z.enum(staffCatalogPricingModels),
    currency: z.string().min(1).max(3).nullish(),
    amountMinor: z.number().int().nonnegative().nullish(),
    minAmountMinor: z.number().int().nonnegative().nullish(),
    maxAmountMinor: z.number().int().nonnegative().nullish(),
    unit: z.enum(staffCatalogPriceUnits).nullish(),
    terms: optionalNullableText(STAFF_CATALOG_BOUNDS.priceTerms),
    source: z.enum(staffCatalogPriceSources).nullish(),
    validFrom: requestInstant.nullish(),
    validUntil: requestInstant.nullish(),
    noExpiry: z.boolean().optional(),
})
    .transform((pricing) => ({
    model: pricing.model,
    currency: pricing.currency ?? null,
    amountMinor: pricing.amountMinor ?? null,
    minAmountMinor: pricing.minAmountMinor ?? null,
    maxAmountMinor: pricing.maxAmountMinor ?? null,
    unit: pricing.unit ?? null,
    terms: pricing.terms ?? null,
    source: pricing.source ?? null,
    validFrom: pricing.validFrom ?? null,
    validUntil: pricing.validUntil ?? null,
    noExpiry: pricing.noExpiry ?? false,
}));
const diningSubtypeSchema = z.strictObject({
    type: z.literal("DINING"),
    dressCode: z.enum(staffCatalogDressCodes).nullish(),
    reservationPolicy: z.enum(staffCatalogReservationPolicies).nullish(),
    reservationNotes: optionalNullableText(1000),
    typicalSpendPerPerson: z
        .strictObject({
        amountMinor: z.number().int().nonnegative(),
        currency: z.string().min(1).max(3),
    })
        .nullish(),
});
const villaSubtypeSchema = z.strictObject({
    type: z.literal("VILLA"),
    bedrooms: whole(0, 50),
    bathrooms: whole(0, 50),
    maxGuests: whole(1, 100),
    minimumStayNights: whole(1, 365),
    checkInTime: z.string().regex(TIME_OF_DAY).nullish(),
    checkOutTime: z.string().regex(TIME_OF_DAY).nullish(),
});
const eventSubtypeSchema = z.strictObject({
    type: z.literal("EVENT"),
    venueName: optionalNullableText(200),
    startsAt: requestInstant,
    endsAt: requestInstant.nullish(),
    timeZone: z.string().min(1).max(64),
    schedulePrecision: z.enum(staffCatalogSchedulePrecisions).nullish(),
    eventStatus: z.enum(staffCatalogEventStatuses).nullish(),
    ticketingNotes: optionalNullableText(1000),
});
const experienceSubtypeSchema = z.strictObject({
    type: z.literal("EXPERIENCE"),
    durationMinutes: whole(1, 10_080),
    privacy: z.enum(staffCatalogExperiencePrivacies).nullish(),
    groupMin: whole(1, 500),
    groupMax: whole(1, 500),
    minimumAge: whole(0, 120),
    eligibilityNotes: optionalNullableText(2000),
    includes: optionalNullableText(2000),
    excludes: optionalNullableText(2000),
    meetingPointText: optionalNullableText(300),
});
const serviceSubtypeSchema = z.strictObject({
    type: z.literal("SERVICE"),
    serviceAreaText: optionalNullableText(300),
    leadTimeHours: whole(0, 8760),
    availabilityNotes: optionalNullableText(1000),
});
/**
 * The full subtype of the item's own type. `type` says which; it must equal the item's type (the use
 * case refuses a mismatch). A field that is left out is saved as empty, so the form sends all of them.
 */
export const staffCatalogSubtypeInputSchema = z.discriminatedUnion("type", [
    diningSubtypeSchema,
    villaSubtypeSchema,
    eventSubtypeSchema,
    experienceSubtypeSchema,
    serviceSubtypeSchema,
]);
export const staffCatalogCategoryLinkInputSchema = z.strictObject({
    code: codeValue,
    isPrimary: z.boolean().optional(),
});
export const staffCatalogTagLinkInputSchema = z.strictObject({
    namespace: z.enum(staffCatalogTagNamespaces),
    code: codeValue,
});
export const staffCatalogPersonLinkInputSchema = z.strictObject({
    slug: slugValue,
    role: z.enum(staffCatalogPersonRoles),
});
export const staffCatalogMediaInputSchema = z.strictObject({
    storageObjectPath: z.string().min(1).max(STAFF_CATALOG_BOUNDS.mediaPath).regex(MEDIA_PATH),
    kind: z.enum(staffCatalogMediaKinds),
    altText: optionalNullableText(STAFF_CATALOG_BOUNDS.mediaAltText),
    caption: optionalNullableText(STAFF_CATALOG_BOUNDS.mediaCaption),
    credit: optionalNullableText(STAFF_CATALOG_BOUNDS.mediaCredit),
    isCover: z.boolean().optional(),
});
// --- request bodies: commands ------------------------------------------------------------------------
const detailFields = {
    name: z.string().min(1).max(STAFF_CATALOG_BOUNDS.name),
    slug: slugValue,
    shortDescription: optionalNullableText(STAFF_CATALOG_BOUNDS.shortDescription),
    description: optionalNullableText(STAFF_CATALOG_BOUNDS.description),
    addressText: optionalNullableText(STAFF_CATALOG_BOUNDS.addressText),
    locality: optionalNullableText(STAFF_CATALOG_BOUNDS.locality),
    region: optionalNullableText(STAFF_CATALOG_BOUNDS.region),
    countryCode: z.string().regex(COUNTRY).nullish(),
    latitude: z.number().min(-90).max(90).nullish(),
    longitude: z.number().min(-180).max(180).nullish(),
    timeZone: z.string().min(1).max(64).nullish(),
    fulfilmentMode: z.enum(staffCatalogFulfilmentModes),
    externalUrl: optionalNullableText(STAFF_CATALOG_BOUNDS.externalUrl),
    editorialPriority: z.number().int().min(0).max(STAFF_CATALOG_BOUNDS.editorialPriority),
};
/**
 * W1. A new item is a DRAFT at version 1. Its source is always `EDITORIAL` (the API sets it, the body
 * cannot). It has no media (an upload needs the item id: add media with W9 after the create) and no
 * external references (W11).
 */
export const staffCatalogCreateItemBodySchema = z.strictObject({
    itemType: z.enum(staffCatalogItemTypes),
    ...detailFields,
    editorialPriority: detailFields.editorialPriority.optional(),
    fulfilmentMode: detailFields.fulfilmentMode.optional(),
    pricing: staffCatalogPricingSchema.optional(),
    subtype: staffCatalogSubtypeInputSchema,
    categories: z
        .array(staffCatalogCategoryLinkInputSchema)
        .max(STAFF_CATALOG_BOUNDS.categories)
        .optional(),
    tags: z.array(staffCatalogTagLinkInputSchema).max(STAFF_CATALOG_BOUNDS.tags).optional(),
    people: z.array(staffCatalogPersonLinkInputSchema).max(STAFF_CATALOG_BOUNDS.people).optional(),
});
/** The fields W2 may change. Send only the fields that changed; a nullable field set to `null` is cleared. */
export const staffCatalogDetailsPatchSchema = z
    .strictObject({
    name: detailFields.name,
    slug: detailFields.slug,
    shortDescription: detailFields.shortDescription,
    description: detailFields.description,
    addressText: detailFields.addressText,
    locality: detailFields.locality,
    region: detailFields.region,
    countryCode: detailFields.countryCode,
    latitude: detailFields.latitude,
    longitude: detailFields.longitude,
    timeZone: detailFields.timeZone,
    fulfilmentMode: detailFields.fulfilmentMode,
    externalUrl: detailFields.externalUrl,
    editorialPriority: detailFields.editorialPriority,
})
    .partial()
    .refine((patch) => Object.keys(patch).length > 0, { message: "the patch is empty" });
export const staffCatalogDetailsBodySchema = z.strictObject({
    expectedVersion,
    patch: staffCatalogDetailsPatchSchema,
});
export const staffCatalogSubtypeBodySchema = z.strictObject({
    expectedVersion,
    subtype: staffCatalogSubtypeInputSchema,
});
export const staffCatalogPricingBodySchema = z.strictObject({
    expectedVersion,
    pricing: staffCatalogPricingSchema,
});
const reason = z.string().min(1).max(STAFF_CATALOG_BOUNDS.reason);
export const staffCatalogPriceAuthorizationBodySchema = z.strictObject({ expectedVersion, reason });
export const staffCatalogCategoriesBodySchema = z.strictObject({
    expectedVersion,
    categories: z.array(staffCatalogCategoryLinkInputSchema).max(STAFF_CATALOG_BOUNDS.categories),
});
export const staffCatalogTagsBodySchema = z.strictObject({
    expectedVersion,
    tags: z.array(staffCatalogTagLinkInputSchema).max(STAFF_CATALOG_BOUNDS.tags),
});
/** The order of the list is the order on the page: position is the index. */
export const staffCatalogPeopleBodySchema = z.strictObject({
    expectedVersion,
    people: z.array(staffCatalogPersonLinkInputSchema).max(STAFF_CATALOG_BOUNDS.people),
});
/**
 * The order of the list is the order on the page: position is the index. A path that is not in the
 * list is removed. A path that is new must come from W10 for this item.
 */
export const staffCatalogMediaBodySchema = z.strictObject({
    expectedVersion,
    media: z.array(staffCatalogMediaInputSchema).max(STAFF_CATALOG_BOUNDS.media),
});
export const staffCatalogUploadTargetBodySchema = z.strictObject({
    contentType: z.enum(staffCatalogUploadContentTypes),
    byteSize: z.number().int().min(1).max(STAFF_CATALOG_BOUNDS.uploadBytes),
});
export const staffCatalogExternalRefBodySchema = z.strictObject({
    expectedVersion,
    provider: z.string().min(1).max(64).regex(PROVIDER),
    externalId: z.string().min(1).max(STAFF_CATALOG_BOUNDS.externalRefId),
    observedAt: requestInstant,
});
/** W12, W14, W15, W16: the transitions and the stamp that need only the version. */
export const staffCatalogVersionBodySchema = z.strictObject({ expectedVersion });
/** W17, W18: unpublish and archive need a reason. */
export const staffCatalogVersionReasonBodySchema = z.strictObject({ expectedVersion, reason });
export const staffCatalogNoteBodySchema = z.strictObject({
    kind: z.enum(staffCatalogNoteKinds),
    body: z.string().min(1).max(STAFF_CATALOG_BOUNDS.noteBody),
});
export const staffCatalogCreateTagBodySchema = z.strictObject({
    namespace: z.enum(staffCatalogTagNamespaces),
    code: codeValue,
    label: z.string().min(1).max(STAFF_CATALOG_BOUNDS.tagLabel),
});
export const staffCatalogCreateCategoryBodySchema = z.strictObject({
    code: codeValue,
    name: z.string().min(1).max(STAFF_CATALOG_BOUNDS.categoryName),
    parentCode: codeValue.nullish(),
    position: z.number().int().min(0).max(999).nullish(),
});
export const staffCatalogCreatePersonBodySchema = z.strictObject({
    slug: slugValue,
    displayName: z.string().min(1).max(STAFF_CATALOG_BOUNDS.personName),
    personKind: z.enum(staffCatalogPersonKinds),
    shortBio: optionalNullableText(STAFF_CATALOG_BOUNDS.personBio),
});
// --- responses: list (R1) ----------------------------------------------------------------------------
/** The keys of one list row, in order. The application exports the same list; `apps/api` compares them. */
export const STAFF_CATALOG_LIST_ITEM_KEYS = [
    "id",
    "itemType",
    "name",
    "slug",
    "status",
    "locality",
    "countryCode",
    "pricingModel",
    "priceClass",
    "fulfilmentMode",
    "sourceKind",
    "editorialPriority",
    "lastVerifiedAt",
    "hasCover",
    "version",
    "updatedAt",
];
export const staffCatalogListItemSchema = z.strictObject({
    id,
    itemType: z.enum(staffCatalogItemTypes),
    name: z.string(),
    slug: z.string(),
    status: z.enum(staffCatalogItemStatuses),
    locality: nullableText,
    countryCode: nullableText,
    pricingModel: z.enum(staffCatalogPricingModels),
    priceClass: z.enum(staffCatalogPriceClasses),
    fulfilmentMode: z.enum(staffCatalogFulfilmentModes),
    sourceKind: z.enum(staffCatalogSourceKinds),
    editorialPriority: count,
    lastVerifiedAt: nullableResponseInstant,
    hasCover: z.boolean(),
    version: count,
    updatedAt: responseInstant,
});
/** A page of items. `nextCursor` is opaque: pass it back as `cursor`. `null` at the end. There is no total. */
export const staffCatalogItemPageSchema = z.strictObject({
    items: z.array(staffCatalogListItemSchema),
    nextCursor: z.string().min(1).max(STAFF_CATALOG_BOUNDS.cursor).nullable(),
});
// --- responses: detail (R2, and W1 to W18) -------------------------------------------------------------
export const staffCatalogItemRecordSchema = z.strictObject({
    id,
    itemType: z.enum(staffCatalogItemTypes),
    status: z.enum(staffCatalogItemStatuses),
    version: count,
    name: z.string(),
    slug: z.string(),
    shortDescription: nullableText,
    description: nullableText,
    addressText: nullableText,
    locality: nullableText,
    region: nullableText,
    countryCode: nullableText,
    latitude: nullableNumber,
    longitude: nullableNumber,
    timeZone: nullableText,
    pricingModel: z.enum(staffCatalogPricingModels),
    priceCurrency: nullableText,
    priceAmountMinor: nullableNumber,
    priceMinAmountMinor: nullableNumber,
    priceMaxAmountMinor: nullableNumber,
    priceUnit: z.enum(staffCatalogPriceUnits).nullable(),
    priceTerms: nullableText,
    priceSource: z.enum(staffCatalogPriceSources).nullable(),
    priceValidFrom: nullableResponseInstant,
    priceValidUntil: nullableResponseInstant,
    priceNoExpiry: z.boolean(),
    priceAuthoritative: z.boolean(),
    priceAuthorizedByStaffUserId: id.nullable(),
    priceAuthorizedAt: nullableResponseInstant,
    fulfilmentMode: z.enum(staffCatalogFulfilmentModes),
    externalUrl: nullableText,
    editorialPriority: count,
    sourceKind: z.enum(staffCatalogSourceKinds),
    lastVerifiedAt: nullableResponseInstant,
    lastVerifiedByStaffUserId: id.nullable(),
    publishedAt: nullableResponseInstant,
    firstPublishedAt: nullableResponseInstant,
    createdByStaffUserId: id,
    statusChangedAt: responseInstant,
    createdAt: responseInstant,
    updatedAt: responseInstant,
});
/** The type's own columns, read back. `type` is the item type. Fields are `null` when empty. */
export const staffCatalogSubtypeRecordSchema = z.discriminatedUnion("type", [
    z.strictObject({
        type: z.literal("DINING"),
        dressCode: z.enum(staffCatalogDressCodes).nullable(),
        reservationPolicy: z.enum(staffCatalogReservationPolicies).nullable(),
        reservationNotes: nullableText,
        typicalSpendPerPerson: z.strictObject({ amountMinor: count, currency: z.string() }).nullable(),
    }),
    z.strictObject({
        type: z.literal("VILLA"),
        bedrooms: nullableNumber,
        bathrooms: nullableNumber,
        maxGuests: nullableNumber,
        minimumStayNights: nullableNumber,
        checkInTime: nullableText,
        checkOutTime: nullableText,
    }),
    z.strictObject({
        type: z.literal("EVENT"),
        venueName: nullableText,
        startsAt: responseInstant,
        endsAt: nullableResponseInstant,
        timeZone: z.string(),
        schedulePrecision: z.enum(staffCatalogSchedulePrecisions),
        eventStatus: z.enum(staffCatalogEventStatuses),
        ticketingNotes: nullableText,
    }),
    z.strictObject({
        type: z.literal("EXPERIENCE"),
        durationMinutes: nullableNumber,
        privacy: z.enum(staffCatalogExperiencePrivacies).nullable(),
        groupMin: nullableNumber,
        groupMax: nullableNumber,
        minimumAge: nullableNumber,
        eligibilityNotes: nullableText,
        includes: nullableText,
        excludes: nullableText,
        meetingPointText: nullableText,
    }),
    z.strictObject({
        type: z.literal("SERVICE"),
        serviceAreaText: nullableText,
        leadTimeHours: nullableNumber,
        availabilityNotes: nullableText,
    }),
]);
export const staffCatalogCategoryLinkSchema = z.strictObject({
    categoryId: id,
    code,
    name: z.string(),
    isPrimary: z.boolean(),
});
export const staffCatalogTagLinkSchema = z.strictObject({
    tagId: id,
    namespace: z.enum(staffCatalogTagNamespaces),
    code,
    label: z.string(),
});
/**
 * `id` changes every time the media list is saved (a save replaces the rows). `storageObjectPath` is
 * the stable key: the admin keys a media row by it.
 */
export const staffCatalogMediaRecordSchema = z.strictObject({
    id,
    kind: z.enum(staffCatalogMediaKinds),
    storageObjectPath: z.string(),
    altText: nullableText,
    caption: nullableText,
    credit: nullableText,
    position: count,
    isCover: z.boolean(),
});
export const staffCatalogMediaViewSchema = z.strictObject({
    record: staffCatalogMediaRecordSchema,
    /** A short-lived signed read URL for the staff preview. `null` when storage is not set up or the file is not an image. */
    previewUrl: z.string().nullable(),
});
export const staffCatalogPersonLinkSchema = z.strictObject({
    personId: id,
    slug: z.string(),
    displayName: z.string(),
    personKind: z.enum(staffCatalogPersonKinds),
    shortBio: nullableText,
    role: z.enum(staffCatalogPersonRoles),
    position: count,
});
export const staffCatalogExternalRefSchema = z.strictObject({
    provider: z.string(),
    externalId: z.string(),
    observedAt: responseInstant,
});
export const staffCatalogSourceSchema = z.strictObject({
    sourceSystem: z.string(),
    sourceKey: z.string(),
    importRowId: id.nullable(),
    createdAt: responseInstant,
});
export const staffCatalogPublishabilityIssueSchema = z.strictObject({
    code: z.enum(staffCatalogPublishabilityIssueCodes),
    severity: z.enum(["BLOCKING", "WARNING"]),
});
/**
 * Everything of an item that a staff member with `catalog.read_internal` may see, and nothing of its
 * internal notes: this top level has no `internalNotes` key, and it must not get one.
 * `publishability.issues` says what would stop a publish right now (BLOCKING) or only deserves a look (WARNING).
 */
export const staffCatalogItemDetailSchema = z.strictObject({
    item: staffCatalogItemRecordSchema,
    subtype: staffCatalogSubtypeRecordSchema.nullable(),
    categories: z.array(staffCatalogCategoryLinkSchema),
    tags: z.array(staffCatalogTagLinkSchema),
    media: z.array(staffCatalogMediaViewSchema),
    people: z.array(staffCatalogPersonLinkSchema),
    externalRefs: z.array(staffCatalogExternalRefSchema),
    sources: z.array(staffCatalogSourceSchema),
    publishability: z.strictObject({ issues: z.array(staffCatalogPublishabilityIssueSchema) }),
    priceClass: z.enum(staffCatalogPriceClasses),
});
// --- responses: notes (R3, W13) -----------------------------------------------------------------------
export const staffCatalogNoteSchema = z.strictObject({
    id,
    kind: z.enum(staffCatalogNoteKinds),
    body: z.string(),
    authorStaffUserId: id,
    createdAt: responseInstant,
});
export const staffCatalogNotesSchema = z.strictObject({ notes: z.array(staffCatalogNoteSchema) });
// --- responses: history (R4) ---------------------------------------------------------------------------
/**
 * One audit event of an item: who, when, what kind of change. `metadata` holds codes, counts and
 * flags, never text a staff member typed. There is no before and after text.
 */
export const staffCatalogHistoryEventSchema = z.strictObject({
    sequence: z.number().int().nonnegative(),
    occurredAt: responseInstant,
    action: z.string(),
    actorKind: z.string(),
    actorId: id.nullable(),
    actorName: nullableText,
    reason: nullableText,
    metadata: z.record(z.string(), z.unknown()),
});
export const staffCatalogHistorySchema = z.strictObject({
    events: z.array(staffCatalogHistoryEventSchema),
    nextCursor: z.string().min(1).max(512).nullable(),
});
// --- responses: taxonomy (R5, R6, R7) -------------------------------------------------------------------
export const staffCatalogCategoryListSchema = z.strictObject({
    items: z.array(z.strictObject({
        code,
        name: z.string(),
        parentCode: code.nullable(),
        position: count,
    })),
});
export const staffCatalogTagListSchema = z.strictObject({
    items: z.array(z.strictObject({
        namespace: z.enum(staffCatalogTagNamespaces),
        code,
        label: z.string(),
    })),
});
export const staffCatalogPersonPageSchema = z.strictObject({
    items: z.array(z.strictObject({
        slug: z.string(),
        displayName: z.string(),
        personKind: z.enum(staffCatalogPersonKinds),
        shortBio: nullableText,
    })),
    nextCursor: z.string().min(1).max(512).nullable(),
});
// --- responses: upload target (W10) ----------------------------------------------------------------------
/**
 * Where the browser sends the file. `storageObjectPath` is the key to put in the W9 list after the
 * upload. The URL and the headers belong to one exact key and expire at `expiresAt`.
 */
export const staffCatalogUploadTargetSchema = z.strictObject({
    storageObjectPath: z.string().min(1).max(STAFF_CATALOG_BOUNDS.mediaPath),
    url: z.string().min(1),
    method: z.enum(["PUT", "POST"]),
    headers: z.record(z.string(), z.string()),
    expiresAt: responseInstant,
});
